import axios from 'axios';

// Define the type for store response
export interface Store {
	storeNumber: string;
	storeName: string;
	marketName: string;
	userLogInStore: string;
}

// Define the type for employee response
export interface Employee {
	displayName: string;
	payrollNumber: string;
}

// Define the type for Borrowed Employee response
export interface BorrowedEmployee {
	lastName: string;
	firstName: string;
	payrollNumber: string;
	loanStartDate: string;
	loanEndDate: string;
}

export interface BorrowedEmployeeResult {
    statusCode: number;
    statusMessage: string;
}

// Fetch stores from API
export const fetchStores = async (): Promise<Store[]> => {
	try {
		const response = await axios.get<Store[]>('/api/BorrowEmployee/stores');
		return response.data;
	} catch (error) {
		console.error("Error fetching stores", error);
		throw new Error("Failed to load stores");
	}
};

// Fetch employees from API based on the selected store and market
export const fetchEmployees = async (storeNumber: string, market: string): Promise<Employee[]> => {
	try {
		const response = await axios.get<Employee[]>(`/api/BorrowEmployee/employees/${storeNumber}`, {
			params: {
				market
			}
		});
		return response.data;
	} catch (error) {
		console.error("Error fetching employees", error);
		throw new Error("Failed to load employees");
	}
};


// Fetch borrowed employees from API without storeNumber as a query param
export const getBorrowedEmployees = async (): Promise<BorrowedEmployee[]> => {
	try {
		const response = await axios.get<BorrowedEmployee[]>(`/api/BorrowEmployee/loadBorrowedEmployees`);
		return response.data;
	} catch (error) {
		console.error("Error fetching borrowed employees", error);
		throw new Error("Failed to load borrowed employees");
	}
};


// Submit BorrowEmployee employee data to the API
export const submitBorrowEmployee = async (storeNumber: string, payrollNumber: string, startDate: string, endDate: string): Promise<BorrowedEmployeeResult> => {
	try {
		const response = await axios.post('/api/BorrowEmployee/SendBorrowEmployee', null, {
			params: {
				storeNumber,
				payrollNumber,
				borrowStartDate: startDate,
				borrowEndDate: endDate
			}, headers: { "Content-Type" : "application/json"}
		});
		return {
			statusCode: response.data.statusCode ?? 98
			, statusMessage: response.data.statusMessage ?? "Unknown status message"};
	} catch (error) {
		console.error("Error submitting borrowed employee", error);
		throw new Error("Failed to submit borrowed employee");
	}
};

export const deleteBorrowedEmployee = async (
	lastName: string,
	firstName: string,
	payrollNumber: string,
	loanStartDate: string,
	loanEndDate: string
): Promise<BorrowedEmployeeResult> => {
	try {
		const response = await axios.post('/api/BorrowEmployee/RemoveBorrowEmployee', null, {
			params: {
				lastName,
				firstName,
				payrollNumber,
				loanStartDate,
				loanEndDate
			}, headers: {
								"Content-Type" : "application/json"
			},
		});


		return {
			statusCode: response.data.statusCode ?? 99,
			statusMessage: response.data.statusMessage ?? "Unknown status message"
		};
	} catch (error) {
		console.error('Error deleting borrowed employee', error);
		throw new Error('Failed to delete borrowed employee');
	}
};

// Map store data to Dropdown-compatible format
export const mapStoresToDropdown = (stores: Store[]) => {
	return stores.map(store => ({
		key: store.storeNumber,
		text: store.storeName, // Only storeName in dropdown
		value: store.storeNumber
	}));
};

// Map employee data to Dropdown-compatible format
export const mapEmployeesToDropdown = (employees: Employee[]) => {
	return employees.map(employee => ({
		key: employee.payrollNumber,
		text: employee.displayName,
		value: employee.payrollNumber
	}));
};
